import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainCard from "../../components/MainCard/MainCard";
import Table from "../../components/Table/Table";
import { IClientCSVData, IMyClientsParams, IRowMyClientsReports, IOption } from "../../constants/interfaces";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import { ClientsStore } from "../../store/MyClientsStore";
import { ROUTE_PATHS } from "../../constants/routePaths";
import { Column, columnTypes, TableOptions } from "../../components/DataTable/datatable.models";
import { Box, Typography } from "@mui/material";
import { getSnapshot, Instance } from "mobx-state-tree";
import { ContextModel, SearchModel } from "../../models/Common";
import { DateTime } from "luxon";
import IClientReports from "../../assets/svg/IClientReports";

import SaveAltIcon from "@mui/icons-material/SaveAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import IWalkingSteps from "../../assets/svg/IWalkingSteps";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import TimerIcon from "@mui/icons-material/Timer";
import { useMediaQuery, useTheme } from "@mui/material";

const StarReport = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const location = useLocation();
  const prevParams = location.state && location.state.keepParams ? getSnapshot(ClientsStore.params) : undefined;
  const pageWasRendered = useRef(false);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [param, setParam] = useState<IMyClientsParams>({
    startDate: prevParams ? prevParams.startDate : undefined,
    endDate: prevParams ? prevParams.endDate : undefined,
    activeProgram: prevParams ? prevParams.activeProgram : undefined,
    orderBy: prevParams ? prevParams.orderBy : undefined,
    orderByDesc: prevParams ? prevParams.orderByDesc : "programStartDate",
    fullName: prevParams ? prevParams.fullName : undefined,
  });
  const [rows, setRows] = useState<IRowMyClientsReports[] | []>([]);
  const [context, setContext] = useState<Instance<typeof ContextModel> | null>();
  const [groupedOptions, setGroupedOptions] = useState<
    {
      group: string;
      id?: number;
      label: string;
      value: string;
    }[]
  >([]);
  const [defaultGroupedOptions, setDefaultGroupedOptions] = useState<any>();

  const autocompleteStatus: IOption[] = [
    { label: "Active", value: "active" },
    { label: "Cancelled", value: "cancelled" },
  ];
  const isVIPClient: boolean = false; //clientType === "VIP"; Commented at the moment, will back in next releases
  const [searchItems, setSearchItems] = useState<Instance<typeof SearchModel>[]>([]);
  const [defaultSearch, setDefaultSearch] = useState<Instance<typeof SearchModel>>();

  const getArrowIndicator = (trendIndicator: string) => {
    let arrow;
    if (trendIndicator === "UP") {
      arrow = String.fromCharCode(8599);
    } else if (trendIndicator === "DOWN") {
      arrow = String.fromCharCode(8600);
    } else {
      arrow = String.fromCharCode(8594);
    }

    return (
      <Box component="span" sx={{ ml: 0.5, fontWeight: "bold", color: "inherit" }}>
        {arrow}
      </Box>
    );
  };

  const renderStarCell = (categoryKey: string) => (context: any) => {
    const item = context?.row?.original;
    const categoryData = item ? item[categoryKey] : null;

    const stars = categoryKey === "stars" ? (item?.stars ?? 0) : (categoryData?.stars ?? 0);
    const trend = categoryKey === "stars" ? item?.trendIndicator : categoryData?.trendIndicator;

    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
          {stars}
        </Typography>
        {getArrowIndicator(trend)}
      </Box>
    );
  };

  /**
   * columns object to be passed to the Table component with the settings for each one.
   */
  const columns: Column[] = [
    {
      name: "Name",
      value: "fullName",
      sortable: false,
      mobile: true,
    },
    {
      name: "Total",
      value: "stars",
      type: columnTypes.CUSTOM,
      renderCell: renderStarCell("stars"),
      align: "center",
      sortable: false,
      mobile: true,
    },
    {
      name: "",
      value: "workout.stars",
      type: columnTypes.CUSTOM,
      renderHeader: () => <FitnessCenterIcon fontSize="large" sx={{ color: "#651FFF" }} />,
      renderCell: renderStarCell("workout"),
      align: "center",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "food.stars",
      type: columnTypes.CUSTOM,
      renderHeader: () => <RestaurantIcon fontSize="large" sx={{ color: "#D50000" }} />,
      renderCell: renderStarCell("food"),
      align: "center",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "walking.stars",
      type: columnTypes.CUSTOM,
      renderHeader: () => <IWalkingSteps width="1.57em" height="1.57em" />,
      renderCell: renderStarCell("walking"),
      align: "center",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "fasting.stars",
      type: columnTypes.CUSTOM,
      renderHeader: () => <TimerIcon fontSize="large" sx={{ color: "#0000006B" }} />,
      renderCell: renderStarCell("fasting"),
      align: "center",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "water.stars",
      type: columnTypes.CUSTOM,
      renderHeader: () => <WaterDropIcon fontSize="large" sx={{ color: "#1E88E5" }} />,
      renderCell: renderStarCell("water"),
      align: "center",
      sortable: false,
      mobile: false,
    },
    {
      name: "Last Login",
      value: "lastLoginDate",
      type: columnTypes.DATE,
      sortable: true,
      mobile: false,
    },
    {
      name: "Start Date",
      value: "programStartDate",
      sortable: true,
      mobile: false,
    },
    {
      name: "Program",
      value: "activeProgram",
      sortable: false,
      mobile: false,
    },
    {
      name: "",
      value: "",
      type: columnTypes.CUSTOM,
      renderCell: () => <RemoveRedEyeOutlined sx={{ fontSize: "2rem" }} className="icon-column" />,
      mobile: true,
    },
  ];

  const getOption = (option: any) => {
    setParam({ ...param, fullName: option?.value ?? undefined });
  };

  /**
   * It will be called on every row click
   */
  const onRowClicked = (row: object) => {
    navigate(`${ROUTE_PATHS.MY_CLIENTS_TABS.replace(":id", (row as IRowMyClientsReports)?.id.toString())}`, {
      state: { backLinkText: "Client Report", backLinkUrl: ROUTE_PATHS.STAR_REPORT },
    });
  };

  /**
   * It will be called every time the sorting or the pagination changed.
   */
  const onTableOptionsChanged = (tableParams: TableOptions) => {
    //save table pagination and sorting
    setParam({
      ...param,
      page: tableParams.page,
      size: tableParams.pageSize,
      orderBy: tableParams.sortBy,
      orderByDesc: tableParams.sortByDesc,
    });
  };

  const getDate = (startDate: string | undefined, endDate: string | undefined) => {
    setParam({ ...param, startDate, endDate });
  };

  const getDataForCSV = async (): Promise<IClientCSVData[]> => {
    const paramToCSV = { ...param, page: undefined, size: undefined, withoutPagination: true };
    const data = await ClientsStore.fetchClientReports(paramToCSV, "star", true);
    return data ?? [];
  };

  const removeKeys = (keysToRemove: Array<keyof IMyClientsParams>) => {
    const newObj = { ...param, status: "active" };

    keysToRemove.forEach((key) => {
      delete newObj[key];
    });

    return newObj;
  };

  const getGroupedOptions = (dataFilter: IOption[]) => {
    const options: IOption[] = [...autocompleteStatus, ...dataFilter];

    const groupByStatus = options.reduce(
      (groups, option) => {
        if (option.value !== "active" && option.value !== "cancelled") {
          groups["Active Programs"] = groups["Active Programs"] || [];
          groups["Active Programs"].push(option);
        }
        return groups;
      },
      {} as Record<string, IOption[]>
    );
    const groupedOptions = Object.entries(groupByStatus).flatMap(([group, items]) =>
      items.map((item) => ({ ...item, group }))
    );
    setGroupedOptions(groupedOptions);
    if (prevParams?.activeProgram) {
      const selectedAP = (prevParams.activeProgram as number[]).map((ap) => {
        return groupedOptions.find((item) => item.id === ap);
      });
      setDefaultGroupedOptions(selectedAP);
    }
  };

  const getFilters = (options: any) => {
    let newActivePrograms: number[] | undefined = [];

    options.forEach((option: any) => {
      if (option.group === "Active Programs") {
        const programValue = option.id;
        if (newActivePrograms && programValue && !newActivePrograms.includes(programValue)) {
          newActivePrograms.push(programValue);
        }
      }
    });

    setParam((prevParam: any) => ({
      ...prevParam,
      activeProgram: newActivePrograms?.length === 0 ? undefined : newActivePrograms,
      VIP: isVIPClient,
    }));
  };

  const getActivePrograms = async () => {
    try {
      await ClientsStore.fetchActivePrograms(
        removeKeys([
          "page",
          "size",
          "fullName",
          "orderBy",
          "orderByDesc",
          "activeProgram",
          "status",
          "startDate",
          "endDate",
        ]) as IMyClientsParams
      );
      const dataFilter: IOption[] = ClientsStore.activePrograms.map((program) => ({
        id: program.id,
        label: program.name,
        value: program.value,
      }));

      getGroupedOptions(dataFilter);
    } catch (error) {
      console.log(error);
    }
  };

  const getStarReport = async () => {
    try {
      const result = await ClientsStore.fetchStarReport(param);
      setRows(result.items);
      setContext(result.context);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const getSearchClientName = async () => {
    await ClientsStore.fetchSearch({ status: "active" });
    setSearchItems(ClientsStore.search);
    if (prevParams?.fullName) {
      const selectedUser = ClientsStore.search.find((item) => item.value === prevParams.fullName);
      setDefaultSearch(selectedUser);
    }
  };

  useEffect(() => {
    ClientsStore.clear();
    const fetchData = async () => {
      setLoadingTable(true);
      await Promise.all([getSearchClientName(), getActivePrograms()]);
    };
    fetchData();
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pageWasRendered.current) {
      const fetchData = async () => {
        setLoadingTable(true);
        try {
          await getStarReport();
        } catch (error) {
          console.log(error);
        } finally {
          setLoadingTable(false);
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  useEffect(() => {
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = (
    <MainCard>
      <Table
        isStarsReport={true}
        getCSVData={getDataForCSV}
        idTable="star-table"
        header={
          <Box
            sx={{
              padding: 1.25,
              fontSize: "1.4rem",
              textAlign: "left",
              marginBottom: 0.5,
              borderBottom: 2,
              borderColor: "divider",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>Star Report:</span>
            {" Change dates to see activity between date ranges. Trend arrows compare to previous time period."}
          </Box>
        }
        tableProps={{
          columns: columns,
          data: rows,
          loading: loadingTable,
          totalRows: context?.totalRows ?? 0,
          orderBy: prevParams && prevParams.orderBy ? prevParams.orderBy : undefined,
          orderByDesc: prevParams && prevParams.orderByDesc ? prevParams.orderByDesc : "programStartDate",
          handleTableOptionsChanged: onTableOptionsChanged,
          handleRowClicked: onRowClicked,
          expandRowOnMobileBreakpoint: true,
          mobileBreakpoint: 900,
          paginationValue:
            prevParams && prevParams.page && prevParams.size
              ? {
                  page: prevParams.page - 1,
                  size: prevParams.size,
                }
              : undefined,
        }}
        calendarProps={{
          withCalendarRange: true,
          sx: { maxWidth: "18rem" },
          customOnChange: getDate,
          startDate: prevParams?.startDate ? DateTime.fromISO(prevParams.startDate) : DateTime.now().minus({ days: 8 }),
          endDate: prevParams?.endDate ? DateTime.fromISO(prevParams.endDate) : DateTime.now().minus({ days: 1 }),
        }}
        autocompleteFilterProps={{
          multiValue: defaultGroupedOptions,
          withInputAutocompleteMulti: true,
          iconMulti: FilterAltIcon,
          optionsMulti: groupedOptions,
          labelMulti: "Active Program",
          placeholderMulti: "Select Program",
          sxMulti: { width: "30rem" },
          OnChangeMulti: getFilters,
          idMulti: "clients-input-filter",
        }}
        downloadCSVButtonProps={{
          id: "client-btn-dowload",
          show: true,
          text: "Download",
          disabled: rows?.length === 0 || (context?.totalRows && context?.totalRows > 500) ? true : false,
          startIcon: <SaveAltIcon />,
          variant: "outlined",
          size: "large",
        }}
        buttonsProps={[
          {
            id: "client-btn-reports",
            show: true,
            text: "Client Reports",
            startIcon: <IClientReports width="2rem" height="2rem" />,
            variant: "outlined",
            size: "large",
            className: "five-inputs",
            onClick: () => {
              navigate(`${ROUTE_PATHS.CLIENT_REPORTS}`);
            },
          },
        ]}
      />
    </MainCard>
  );

  return content;
};

export default StarReport;
